/**
 * Registro Público de Panamá — tipos, datos de ejemplo (demo) y un asistente
 * DETERMINISTA (sin modelo) para la herramienta de consulta.
 *
 * El scraper real vive en el servicio `services/rp-consulta` (Selenium + FastAPI);
 * aquí sólo están el contrato de datos que consume la UI y un ayudante 100%
 * reglado — la misma filosofía del explicador de la matriz de riesgo en `demo.ts`:
 * el texto se compone verbatim de reglas conocidas, ningún LLM interviene.
 */

export type RpSeccion = 'mercantil' | 'personas';

export type RpVigencia =
  | 'vigente'
  | 'suspendido'
  | 'disuelto'
  | 'fusionado'
  | 'redomiciliado'
  | 'desconocido';

export interface RpMiembro {
  cargo: string;
  nombre: string;
}

export interface RpPrelacion {
  entrada: string;
  tramite: string;
  categoria: string;
  estado: string;
  entregado: boolean;
}

export type RpCotejoResultado = 'coincide' | 'discrepancia' | 'pendiente_ocr' | 'no_verificable';

export interface RpConsultaInput {
  seccion: RpSeccion;
  folio: string;
  nombre?: string | undefined;
}

// ---------------------------------------------------------------------------
// Login del Registro Público + descarga por lote (proxy al servicio rp-consulta).
// Las credenciales las digita el usuario y se envían solo en la petición de login;
// el navegador nunca las guarda (I6/I7). El servicio hace el scraping.
// ---------------------------------------------------------------------------

export interface RpLoginInput {
  usuario: string;
  password: string;
  twocaptchaApiKey?: string | undefined;
  recaptchaSitekey?: string | undefined;
}

/** Capacidades del servicio (de GET /salud) para mostrar avisos beta honestos. */
export interface RpSalud {
  ok: boolean;
  sesion_guardada?: boolean;
  beta?: {
    scrape_disponible: boolean;
    ocr_disponible: boolean;
    requiere_2captcha: boolean;
    nota: string;
  };
}

/** Un archivo XLSX devuelto por el API como base64 (plantilla o resultado del lote). */
export interface RpArchivo {
  fileBase64: string;
  contentType: string;
  fileName: string;
}

/** Resultado de una descarga por lote: el XLSX + los conteos de folios. */
export interface RpLoteResult extends RpArchivo {
  ok: number;
  errores: number;
}

export interface RpConsultaResult {
  seccion: RpSeccion;
  folio: string;
  denominacion: string;
  vigencia: RpVigencia;
  estadoRaw: string;
  fechaInscripcion: string;
  tipoOrganizacion: string;
  domicilio: string;
  miembros: RpMiembro[];
  prelacion: RpPrelacion[];
  cotejo: { resultado: RpCotejoResultado; detalle: string };
  fuente: string;
  /** true cuando proviene del set de demostración (sin backend real). */
  demo: boolean;
  consultadoEn: string;
}

// ---------------------------------------------------------------------------
// Etiquetas y tono para la UI (Pill tones: green | amber | red | slate)
// ---------------------------------------------------------------------------

export const VIGENCIA_LABEL: Record<RpVigencia, string> = {
  vigente: 'Vigente',
  suspendido: 'Suspendida',
  disuelto: 'Disuelta',
  fusionado: 'Fusionada',
  redomiciliado: 'Redomiciliada',
  desconocido: 'No consta',
};

export const VIGENCIA_TONE: Record<RpVigencia, 'green' | 'amber' | 'red' | 'slate'> = {
  vigente: 'green',
  suspendido: 'amber',
  disuelto: 'red',
  fusionado: 'red',
  redomiciliado: 'red',
  desconocido: 'slate',
};

export const COTEJO_LABEL: Record<RpCotejoResultado, string> = {
  coincide: 'Miembros corroborados',
  discrepancia: 'Discrepancia — revisar',
  pendiente_ocr: 'Pendiente de OCR',
  no_verificable: 'No verificable',
};

export const COTEJO_TONE: Record<RpCotejoResultado, 'green' | 'amber' | 'red' | 'slate'> = {
  coincide: 'green',
  discrepancia: 'red',
  pendiente_ocr: 'amber',
  no_verificable: 'slate',
};

// ---------------------------------------------------------------------------
// Vigilancia semanal (alarma): subsectores y modelo de folio vigilado
// ---------------------------------------------------------------------------

/** El "subsector" que identifica de qué es la alerta registral. */
export type RpSubsector = 'vigencia' | 'miembros' | 'fusion' | 'prelacion' | 'directores';

/** Nombre de la pestaña — se usa como identificación de las alertas que genera. */
export const VIGILANCIA_TAB = 'Vigilancia Registral';

export const SUBSECTOR_LABEL: Record<RpSubsector, string> = {
  vigencia: 'Vigencia',
  miembros: 'Miembros',
  fusion: 'Fusión',
  prelacion: 'Prelación',
  directores: 'Directores',
};

/** Un folio marcado para revisión semanal. */
export interface RpWatch {
  id: string;
  seccion: RpSeccion;
  folio: string;
  denominacion: string;
  addedAt: string;
  lastCheckedAt: string | null;
  nextCheckAt: string;
  /** cuántas alertas ha levantado este folio. */
  hallazgos: number;
}

export interface RpSignal {
  subsector: RpSubsector;
  prioridad: 'Alta' | 'Media' | 'Baja';
  detalle: string;
}

export interface RpWeeklyCheckResult {
  revisados: number;
  nuevasAlertas: number;
  alertas: {
    folio: string;
    denominacion: string;
    subsector: RpSubsector;
    prioridad: 'Alta' | 'Media' | 'Baja';
    detalle: string;
    alertId: string;
  }[];
}

// ---------------------------------------------------------------------------
// Barrer mi cartera: barrido registral sobre los folios de la cartera de clientes.
// El servicio abre el folio de cada expediente en el Registro Público, detecta
// cambios (vigencia/fusión/prelación) y levanta una alerta por cambio.
// ---------------------------------------------------------------------------

/** Una fila del barrido: el expediente cotejado y el resultado en el Registro. */
export interface RpSweepRow {
  expedienteId: string;
  nombre: string;
  folio: string;
  estado: string;
  cambio: boolean;
  alerta: boolean;
  error?: string | undefined;
}

/** Resumen del barrido de cartera + el detalle por expediente. */
export interface RpSweepResult {
  revisados: number;
  cambios: number;
  errores: number;
  sinFolio: number;
  resultados: RpSweepRow[];
}

/**
 * Evalúa un folio y devuelve las señales relevantes para compliance — la lógica
 * DETERMINISTA que corre el chequeo semanal. Todo se deriva del resultado
 * (vigencia, prelación) — sin modelo. En producción se compararía además contra
 * el snapshot anterior para detectar cambios de miembros/directores.
 */
export function evaluarFolio(r: RpConsultaResult): RpSignal[] {
  const out: RpSignal[] = [];
  if (r.vigencia === 'fusionado') {
    out.push({ subsector: 'fusion', prioridad: 'Alta', detalle: `Fusionada — «${r.estadoRaw}». Revise quién la absorbió en Prelación.` });
  } else if (r.vigencia === 'disuelto') {
    out.push({ subsector: 'vigencia', prioridad: 'Alta', detalle: `Disuelta — «${r.estadoRaw}». No inicie ni continúe la relación sin aclararlo.` });
  } else if (r.vigencia !== 'vigente') {
    out.push({ subsector: 'vigencia', prioridad: 'Media', detalle: `Estado no vigente — «${r.estadoRaw}».` });
  }
  // fusión detectada en un trámite de Prelación aunque la vigencia no la marque
  const fus = r.prelacion.find((p) => /fusi[oó]n/i.test(p.tramite));
  if (fus && r.vigencia !== 'fusionado') {
    out.push({ subsector: 'fusion', prioridad: 'Alta', detalle: `Trámite de fusión en Prelación (${fus.entrada}).` });
  }
  const pend = r.prelacion.filter((p) => !p.entregado);
  if (pend.length > 0) {
    out.push({
      subsector: 'prelacion',
      prioridad: 'Media',
      detalle: `${pend.length} trámite(s) de Prelación sin entregar (${pend.map((p) => p.entrada).join(', ')}).`,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Datos de ejemplo (FICTICIOS — no corresponden a ningún folio real)
// ---------------------------------------------------------------------------

const FUENTE = 'Registro Público de Panamá · www.rp.gob.pa (sesión autenticada)';

/** Folios de muestra pre-cargados para la demo (denominaciones y personas ficticias). */
export const RP_EJEMPLOS: RpConsultaResult[] = [
  {
    seccion: 'mercantil',
    folio: '2588041',
    denominacion: 'INVERSIONES DEL ISTMO, S.A.',
    vigencia: 'vigente',
    estadoRaw: 'VIGENTE',
    fechaInscripcion: '14/03/2018',
    tipoOrganizacion: 'SOCIEDAD ANÓNIMA',
    domicilio: 'CORREGIMIENTO BELLA VISTA, DISTRITO PANAMÁ, PROVINCIA PANAMÁ',
    miembros: [
      { cargo: 'Suscriptor', nombre: 'AGENTE SUSCRIPTOR 1, S.A.' },
      { cargo: 'Suscriptor', nombre: 'AGENTE SUSCRIPTOR 2, S.A.' },
      { cargo: 'Agente Residente', nombre: 'ESTUDIO LEGAL EJEMPLO' },
      { cargo: 'Director / Presidente', nombre: 'ANA PATRICIA GÓMEZ REYES' },
      { cargo: 'Director / Secretario', nombre: 'LUIS ALBERTO CEDEÑO PÉREZ' },
      { cargo: 'Director / Tesorero', nombre: 'MARÍA JOSÉ RIVERA SANTOS' },
    ],
    prelacion: [
      {
        entrada: '120045/2018 (0)',
        tramite: 'Constitución de Capital de Sociedades, Tasa Única, Derechos de Calificación',
        categoria: 'pacto',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
      {
        entrada: '338920/2023 (0)',
        tramite: 'Acta de Sociedad Mercantil, Derechos de Calificación',
        categoria: 'acta',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
      {
        entrada: '451077/2025 (0)',
        tramite: 'Acta de Sociedad Mercantil, Cambio de Directores',
        categoria: 'acta',
        estado: 'En Trámite',
        entregado: false,
      },
    ],
    cotejo: {
      resultado: 'pendiente_ocr',
      detalle:
        '2 documento(s) de prelación relevantes sin OCR aún — ejecute la consulta con OCR para cotejar los miembros contra las escrituras.',
    },
    fuente: FUENTE,
    demo: true,
    consultadoEn: '2026-07-03',
  },
  {
    seccion: 'mercantil',
    folio: '155123456',
    denominacion: 'FUNDACIÓN CAMINO CLARO',
    vigencia: 'suspendido',
    estadoRaw: 'SUSPENDIDO POR FALTA DE PAGO DE TASA ÚNICA',
    fechaInscripcion: '02/09/2015',
    tipoOrganizacion: 'FUNDACIÓN DE INTERÉS PRIVADO',
    domicilio: 'CORREGIMIENTO SAN FRANCISCO, DISTRITO PANAMÁ, PROVINCIA PANAMÁ',
    miembros: [
      { cargo: 'Fundador', nombre: 'FUNDADOR EJEMPLO, S.A.' },
      { cargo: 'Agente Residente', nombre: 'ESTUDIO LEGAL EJEMPLO' },
      { cargo: 'Miembro del Consejo', nombre: 'CARLOS ENRIQUE MORENO DÍAZ' },
      { cargo: 'Miembro del Consejo', nombre: 'SOFÍA ELENA VÁSQUEZ LEÓN' },
    ],
    prelacion: [
      {
        entrada: '210934/2015 (0)',
        tramite: 'Constitución de Fundación de Interés Privado, Tasa Única',
        categoria: 'pacto',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
    ],
    cotejo: {
      resultado: 'coincide',
      detalle: 'Los miembros del consejo coinciden con la escritura de constitución en Prelación.',
    },
    fuente: FUENTE,
    demo: true,
    consultadoEn: '2026-07-03',
  },
  {
    seccion: 'mercantil',
    folio: '1650944',
    denominacion: 'COMERCIAL UNIDA, S.A.',
    vigencia: 'fusionado',
    estadoRaw: 'FUSIONADA — ABSORBIDA POR GRUPO CONTINENTAL, S.A.',
    fechaInscripcion: '21/06/2012',
    tipoOrganizacion: 'SOCIEDAD ANÓNIMA',
    domicilio: 'CORREGIMIENTO CALIDONIA, DISTRITO PANAMÁ, PROVINCIA PANAMÁ',
    miembros: [
      { cargo: 'Agente Residente', nombre: 'ESTUDIO LEGAL EJEMPLO' },
      { cargo: 'Director / Presidente', nombre: 'RICARDO ALFONSO TEJADA MORA' },
      { cargo: 'Director / Secretario', nombre: 'GABRIELA ISABEL PINTO ARJONA' },
    ],
    prelacion: [
      {
        entrada: '145880/2012 (0)',
        tramite: 'Constitución de Capital de Sociedades, Tasa Única',
        categoria: 'pacto',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
      {
        entrada: '502233/2024 (0)',
        tramite: 'Acta de Fusión por Absorción',
        categoria: 'fusion',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
    ],
    cotejo: {
      resultado: 'coincide',
      detalle: 'La fusión por absorción consta en la escritura de Prelación (entrada 502233/2024): absorbida por GRUPO CONTINENTAL, S.A.',
    },
    fuente: FUENTE,
    demo: true,
    consultadoEn: '2026-07-03',
  },
];

/** El folio de muestra que la UI ofrece con «Ver ejemplo». */
export const RP_EJEMPLO_FOLIO = RP_EJEMPLOS[0]!.folio;

const soloDigitos = (s: string): string => s.replace(/\D/g, '');

/**
 * Resuelve una consulta de demostración de forma DETERMINISTA:
 * - si el folio coincide con un ejemplo pre-cargado, lo devuelve;
 * - si no, sintetiza un resultado VIGENTE plausible con el folio/nombre pedidos,
 *   para que la demo siempre muestre algo coherente (marcado `demo: true`).
 */
export function resolverConsultaDemo(input: RpConsultaInput): RpConsultaResult {
  const folio = soloDigitos(input.folio) || input.folio.trim();
  const exacto = RP_EJEMPLOS.find((e) => soloDigitos(e.folio) === folio && e.seccion === input.seccion);
  if (exacto) return { ...exacto, consultadoEn: new Date().toISOString().slice(0, 10) };

  const nombre = (input.nombre ?? '').trim();
  const denominacion =
    nombre || (input.seccion === 'personas' ? 'PERSONA NATURAL DE EJEMPLO' : 'SOCIEDAD DE EJEMPLO, S.A.');
  return {
    seccion: input.seccion,
    folio: input.folio.trim(),
    denominacion,
    vigencia: 'vigente',
    estadoRaw: 'VIGENTE',
    fechaInscripcion: '10/01/2020',
    tipoOrganizacion: input.seccion === 'personas' ? 'PERSONA NATURAL' : 'SOCIEDAD ANÓNIMA',
    domicilio: 'CORREGIMIENTO CIUDAD DE PANAMÁ, DISTRITO PANAMÁ, PROVINCIA PANAMÁ',
    miembros: [
      { cargo: 'Agente Residente', nombre: 'ESTUDIO LEGAL EJEMPLO' },
      { cargo: 'Director / Presidente', nombre: 'REPRESENTANTE DE EJEMPLO' },
    ],
    prelacion: [
      {
        entrada: '100000/2020 (0)',
        tramite: 'Constitución, Tasa Única, Derechos de Calificación',
        categoria: 'pacto',
        estado: 'Entregado como Trámite Agotado',
        entregado: true,
      },
    ],
    cotejo: {
      resultado: 'pendiente_ocr',
      detalle: 'Ejecute la consulta con OCR para cotejar los miembros contra los documentos de Prelación.',
    },
    fuente: `${FUENTE} · resultado de demostración`,
    demo: true,
    consultadoEn: new Date().toISOString().slice(0, 10),
  };
}

// ---------------------------------------------------------------------------
// Asistente DETERMINISTA de la herramienta (sin LLM)
// ---------------------------------------------------------------------------

export interface HelperBlock {
  title: string;
  /** Líneas: soportan **negrita** al renderizar. */
  lines: string[];
}

/** Guía de uso (estado inicial, sin resultado aún). */
export function rpComoUsar(): HelperBlock {
  return {
    title: 'Cómo usar esta herramienta',
    lines: [
      '**1.** Elige el tipo: **Mercantil** para sociedades y fundaciones, **Personas** para personas naturales.',
      '**2.** Ingresa el **número de folio** (o el **nombre/denominación** si no lo tienes).',
      '**3.** Presiona **Consultar**. La herramienta abre el folio en el Registro Público y arma un informe verificado.',
      'Obtienes: **Vigencia** (activa/suspendida/disuelta/**fusionada** — y quién la absorbió), los **miembros** (junta directiva, agente residente, suscriptores), la **Prelación** (todos los trámites y su estado de entrega) y el **cotejo** de los miembros contra las escrituras.',
      'Los PDFs de Prelación se descargan y quedan citados por entrada — puedes adjuntarlos al expediente del cliente.',
      'Puedes marcar un folio para **vigilancia semanal**: lo revisamos cada semana y, ante un cambio (vigencia, fusión, prelación…), se genera una alerta en **Alertas y Casos**.',
    ],
  };
}

/**
 * Lectura DETERMINISTA del resultado: interpreta la vigencia, los miembros, la
 * prelación y el cotejo, y sugiere próximos pasos — todo derivado de los datos,
 * sin modelo. Devuelve dos bloques: «Lectura del resultado» y «Qué hacer ahora».
 */
export function rpInterpretar(r: RpConsultaResult): HelperBlock[] {
  const lectura: string[] = [];

  // Vigencia
  const vig: Record<RpVigencia, string> = {
    vigente: `**${r.denominacion}** está **VIGENTE**: la persona jurídica está activa y en regla en el Registro Público.`,
    suspendido: `**${r.denominacion}** está **SUSPENDIDA** («${r.estadoRaw}»): la sociedad existe pero tiene una restricción activa — verifica el motivo antes de operar con ella.`,
    disuelto: `**${r.denominacion}** está **DISUELTA**: la persona jurídica ya no está activa. No debería iniciarse una relación comercial sin aclararlo.`,
    fusionado: `**${r.denominacion}** fue **FUSIONADA**: fue absorbida por otra sociedad. Revisa el documento de fusión en Prelación para identificar quién la absorbió.`,
    redomiciliado: `**${r.denominacion}** fue **REDOMICILIADA**: se trasladó a otra jurisdicción. Confirma su estatus en el país de destino.`,
    desconocido: `No se pudo determinar la vigencia de **${r.denominacion}** con certeza — revisa manualmente en el Registro Público.`,
  };
  lectura.push(vig[r.vigencia]);

  // Miembros
  if (r.miembros.length > 0) {
    const directores = r.miembros.filter((m) => /director|presidente|secretario|tesorero|consejo|dignatario/i.test(m.cargo));
    const agente = r.miembros.find((m) => /agente residente/i.test(m.cargo));
    lectura.push(
      `Tiene **${r.miembros.length}** miembro(s) registrados` +
        (directores.length ? ` — incluye ${directores.length} de junta/consejo` : '') +
        (agente ? `, con agente residente **${agente.nombre}**.` : '.') +
        ' Estos son los nombres a cotejar contra tus listas de sanciones y PEP.'
    );
  } else {
    lectura.push('No se listaron miembros — puede tratarse de una persona natural o de un folio sin junta registrada.');
  }

  // Prelación
  const total = r.prelacion.length;
  const pendientes = r.prelacion.filter((p) => !p.entregado);
  if (total > 0) {
    lectura.push(
      `Prelación: **${total}** trámite(s)` +
        (pendientes.length
          ? `, de los cuales **${pendientes.length}** aún no están «entregados» (${pendientes
              .map((p) => p.entrada)
              .join(', ')}) — verifica que estén en orden.`
          : ' — todos entregados/agotados.')
    );
  }

  // Cotejo
  lectura.push(`Cotejo Miembros ↔ Prelación: **${COTEJO_LABEL[r.cotejo.resultado]}**. ${r.cotejo.detalle}`);

  // Próximos pasos
  const pasos: string[] = [];
  if (r.vigencia === 'vigente') {
    pasos.push('Adjunta este informe y los PDFs de Prelación al **expediente** del cliente como evidencia de debida diligencia.');
  } else {
    pasos.push('Documenta la condición de vigencia en el expediente y **escala a Cumplimiento** antes de continuar.');
  }
  if (r.cotejo.resultado === 'pendiente_ocr') {
    pasos.push('Ejecuta la consulta **con OCR** para cotejar automáticamente los miembros contra las escrituras.');
  }
  pasos.push('Corre **Validación masiva** con los nombres de los miembros para descartar coincidencias en listas/PEP.');
  if (pendientes.length > 0) {
    pasos.push('Da seguimiento a los trámites de Prelación aún **no entregados**.');
  }

  return [
    { title: 'Lectura del resultado', lines: lectura },
    { title: 'Qué hacer ahora', lines: pasos },
  ];
}

/** Preguntas frecuentes con respuesta canned (deterministas, como el explicador de matriz). */
// Notas OPERATIVAS (no definiciones obvias): cuándo actuar, límites beta y cómo
// aprovechar la herramienta — lo que un oficial no sabe de memoria.
export const RP_FAQ: { q: string; a: string }[] = [
  {
    q: '¿Cuándo un hallazgo registral obliga a actuar?',
    a: 'Cuando durante la relación aparece una **disolución** o **suspensión**, un **cambio de junta directiva** hacia nominados/testaferros, o una **fusión** que cambia al controlante real: son señales de revisión y **posible ROS** (Ley 23). Nunca es rechazo automático — documente y decida con revisión humana (debido proceso).',
  },
  {
    q: 'Una **discrepancia** en el cotejo, ¿qué significa?',
    a: 'Que los miembros en pantalla **no coinciden** con la escritura (PDF de Prelación). Suele indicar un cambio de junta o de **beneficiario final no reflejado**. Verifique la última acta, actualice el BO en el expediente y re-corra el screening de los nombres nuevos.',
  },
  {
    q: '¿Qué NO cubre todavía (beta)?',
    a: 'El scraping en vivo requiere **iniciar sesión con sus credenciales del Registro Público** + una clave **2Captcha** (el sitio usa reCAPTCHA v2). El **OCR de escrituras está en beta** y puede fallar; cada folio toma segundos. Donde una capacidad no está disponible, la consulta **lo indica** en vez de inventar datos.',
  },
  {
    q: 'Descarga por lote: ¿qué archivo subo?',
    a: 'Un **.xlsx** con columnas **seccion** (mercantil/personas), **folio** y **nombre** (opcional). Descargue la **plantilla** desde la ventana para no adivinar el formato; el resultado sale en un XLSX ordenado por folio, con hojas de Miembros, Prelación y Errores.',
  },
  {
    q: 'Vigilancia semanal ≠ tiempo real',
    a: 'Marcar **Vigilar este folio** hace un **barrido semanal** (no monitoreo en vivo): ante cambio de vigencia, fusión o prelación pendiente, abre una alerta en **Alertas y Casos**. Ate esa cadencia a la **renovación de DDC** del cliente para que la revisión registral y la de expediente caigan juntas.',
  },
];
